import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import { IntlShape } from 'react-intl';

import { PurchaseWithBrandName } from '../types';

const getPrice = (amount: string, currency: Currency) => {
  return parseFloat(amount).toFixed(currency === 'JPY' ? 0 : 2);
};
const getOrderPlaced = (purchase: PurchaseWithBrandName, timezone: string) =>
  DateTime.fromISO(purchase.created_at).setZone(timezone).toFormat('dd.MM.yyyy HH:mm');

const getDeliveryTime = (purchase: PurchaseWithBrandName, timezone: string) =>
  purchase.delivery.time !== null
    ? DateTime.fromISO(purchase.delivery.time).setZone(timezone).toFormat('dd.MM.yyyy HH:mm')
    : '–';

const getBrandName = (purchase: PurchaseWithBrandName, intl: IntlShape) =>
  purchase.brandName
    ? {
        [intl.formatMessage({ id: 'purchases.csv.brand-name' })]: purchase.brandName,
      }
    : {};

const getItems = (purchase: PurchaseWithBrandName, intl: IntlShape) =>
  purchase.items
    .map(item =>
      intl.formatMessage(
        {
          id: 'purchases.csv.items-value',
        },
        {
          count: item.count,
          name: item.name,
          price: getPrice(item.unit_price.amount, item.unit_price.currency),
        },
      ),
    )
    .join(', ');

const getPosIds = (purchase: PurchaseWithBrandName) =>
  purchase.items
    .filter(item => !!item.external_id)
    .map(item => `${item.count}x ${item.external_id}`)
    .join(', ');

const getReviewAttributions = (purchase: PurchaseWithBrandName) =>
  purchase.review ? purchase.review.attributions.join(', ') : '';

export const toByDateCSV = (purchases: PurchaseWithBrandName[], timezone: string, intl: IntlShape) => {
  return unparse(
    purchases.map(purchase => {
      return {
        [intl.formatMessage({ id: 'purchases.csv.order-placed' })]: getOrderPlaced(purchase, timezone),
        [intl.formatMessage({ id: 'purchases.csv.order-number' })]: `#${purchase.order_number}`,
        [intl.formatMessage({ id: 'purchases.csv.delivery-status' })]: purchase.delivery.status,
        [intl.formatMessage({ id: 'purchases.csv.delivery-time' })]: getDeliveryTime(purchase, timezone),
        [intl.formatMessage({ id: 'purchases.csv.delivery-type' })]: purchase.delivery.type,
        ...getBrandName(purchase, intl),
        [intl.formatMessage({ id: 'purchases.csv.venue' })]: purchase.restaurant.name,
        [intl.formatMessage({ id: 'purchases.csv.items' })]: getItems(purchase, intl),
        [intl.formatMessage({ id: 'purchases.csv.pos-ids' })]: getPosIds(purchase),
        [intl.formatMessage({ id: 'purchases.csv.price' })]: getPrice(purchase.price.amount, purchase.price.currency),
        [intl.formatMessage({ id: 'purchases.csv.currency' })]: purchase.price.currency,
        [intl.formatMessage({ id: 'purchases.csv.review-score' })]: purchase.review?.score,
        [intl.formatMessage({ id: 'purchases.csv.review-comment' })]: purchase.review?.comment,
        [intl.formatMessage({ id: 'purchases.csv.review-attributions' })]: getReviewAttributions(purchase),
      };
    }),
  );
};
